import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { Button } from 'src/components/Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import colors from 'src/theme/base/colors';
import { ScheduleFacebookPostStep2Props } from './types';
import boxShadows from 'src/theme/base/boxShadows';
import { PostFrequency, PostType } from 'src/DTO/FacebookPost.type';
import { calculateDateDetails, getPostScheduleDescription } from 'src/utils/facebookPostUtils';
import { useLabels } from 'src/hooks/useLabels';
import { DatePicker } from 'src/components/DatePicker';
import { TimePicker } from 'src/components/TimePicker/TimePicker';

export const ScheduleFacebookPostStep2: React.FC<ScheduleFacebookPostStep2Props> = ({
  onSubmit,
  handlePreviousButtonClick,
  postSchedule,
  handleMoveToNextStep,
}) => {
  const maximumEndDate = calculateDateDetails(postSchedule?.startDate);

  const { handleFrequencyLabels, handlePostTypeLabels } = useLabels();

  const handleFrequencyChange = (event: SelectChangeEvent<PostFrequency>) => {
    const frequency = event.target.value as PostFrequency;
    onSubmit({ ...postSchedule, frequency });
  };

  const handlePostType = (event: SelectChangeEvent<PostType>) => {
    const postType = event.target.value as PostType;
    onSubmit({ ...postSchedule, postType });
  };

  const handleTimeChange = (timeValue: string) => {
    const startTime = timeValue || '';
    onSubmit({ ...postSchedule, startTime });
  };

  const handleStartDateChange = (dateValue: string) => {
    const startDate = dateValue || '';
    onSubmit({ ...postSchedule, startDate, endDate: '' });
  };

  const handleEndDateChange = (dateValue: string) => {
    const endDate = dateValue || '';
    onSubmit({ ...postSchedule, endDate });
  };

  const scheduleDescription = useMemo(
    () => getPostScheduleDescription(postSchedule),
    [postSchedule],
  );

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Box sx={{ maxWidth: 'md' }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box mb={4} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="h5">
                <Trans>Válaszd ki az ütemezés beállításait</Trans>
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              width="100%"
              border={`2px solid ${colors.inputColors.borderColor.main}`}
              borderRadius={3}
              boxShadow={boxShadows.sm}
              sx={{
                bgcolor: colors.grey[100],
                p: 2,
              }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
                      Gyakoriság:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <Box sx={{ width: '100%', maxWidth: 250 }}>
                      <FormControl
                        fullWidth
                        sx={{
                          '.MuiInputBase-root': {
                            height: '40px !important',
                            padding: '0 !important',
                          },
                        }}>
                        <Select
                          value={postSchedule?.frequency}
                          onChange={handleFrequencyChange}
                          fullWidth>
                          {Object.values(PostFrequency).map(value => (
                            <MenuItem key={value} value={value}>
                              {handleFrequencyLabels(value)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
                      Kezdési dátum:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <Box sx={{ width: '100%', maxWidth: 250 }}>
                      <DatePicker
                        value={postSchedule?.startDate}
                        handleDateChange={handleStartDateChange}
                        isDisabled={false}
                        maximumEndDate={undefined}
                        defaultCalendarMonth={new Date()}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
                      Kezdési idő:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <Box sx={{ width: '100%', maxWidth: 250 }}>
                      <TimePicker
                        value={postSchedule?.startTime}
                        handleTimeChange={handleTimeChange}
                        isDisabled={!postSchedule?.startDate}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
                      Lejárati dátum:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <Box sx={{ width: '100%', maxWidth: 250 }}>
                      <DatePicker
                        value={postSchedule?.endDate}
                        handleDateChange={handleEndDateChange}
                        isDisabled={!postSchedule?.startDate}
                        maximumEndDate={maximumEndDate}
                        defaultCalendarMonth={postSchedule?.startDate}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
                      Posztolás módja:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <Box sx={{ width: '100%', maxWidth: 250 }}>
                      <FormControl
                        fullWidth
                        sx={{
                          '.MuiInputBase-root': {
                            height: '40px !important',
                            padding: '0 !important',
                          },
                        }}>
                        <Select
                          fullWidth
                          value={postSchedule?.postType || PostType.POST}
                          onChange={handlePostType}>
                          <MenuItem key={PostType.POST} value={PostType.POST}>
                            {handlePostTypeLabels(PostType.POST)}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                  {postSchedule?.startDate && postSchedule?.startTime && postSchedule?.endDate && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
                          Ütemezett posztolás:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} display="flex" justifyContent="right">
                        <Box sx={{ width: '100%', maxWidth: 250 }}>
                          <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}>
                            {scheduleDescription}
                          </Typography>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              </LocalizationProvider>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between">
              <Button onClick={handlePreviousButtonClick}>
                <Trans>generic.back</Trans>
              </Button>
              <Button
                variant={BUTTON_VARIANT.GRADIENT}
                color={BUTTON_COLOR.DARK}
                type="submit"
                onClick={handleMoveToNextStep}
                disabled={
                  !postSchedule.startDate ||
                  !postSchedule.frequency ||
                  !postSchedule.startTime ||
                  !postSchedule.endDate
                }>
                <Trans>jobPage.createRoleForm.submitButtonLabel</Trans>
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
